import { alpha, Theme } from "@mui/material/styles";

// Chrome for the dashboard home's top band. The markets grid and the
// economic calendar are two panels that have to read as ONE instrument:
// same border, same header rail, same hairlines. These are the shared
// tokens so neither drifts from the other.

// Cells sit on a SOLID surface rather than the usual 2% wash so the grid's
// 1px gaps can carry the hairlines without translucency stacking up. The
// values are the house wash resolved against the page: rgba(255,255,255,
// 0.02) over #1a1a1a, and rgba(0,0,0,0.02) over #ffffff.
export const panelSurface = (isDark: boolean): string => (isDark ? "#1f1f1f" : "#fafafa");

export const panelHairline = (isDark: boolean): string =>
  isDark ? "rgba(255, 255, 255, 0.07)" : "rgba(0, 0, 0, 0.07)";

export const panelBorder = (isDark: boolean): string =>
  isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)";

// ─── Overlays: one family for every dialog, sheet, menu and popover ───────
// The ticker page's card grey, so an overlay reads as the same material as
// the app's panels. Dialogs and sheets sit on a backdrop and need no shadow;
// a menu can open over a dialog of the same colour, so menus float.
export const overlaySurface = (isDark: boolean): string => (isDark ? "#212121" : "#ffffff");

export const floatShadow = (isDark: boolean): string =>
  isDark ? "0 12px 32px -8px rgba(0, 0, 0, 0.55)" : "0 12px 32px -8px rgba(0, 0, 0, 0.16)";

// Tooltips everywhere in the screener and notifications: a dark chip with a
// hairline, instead of MUI's default grey.
export const tooltipSx = (isDark: boolean) => ({
  backgroundColor: isDark ? "#2e2e2e" : "#1d1d1f",
  border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
  color: isDark ? "#e5e7eb" : "#ffffff",
  fontSize: "0.75rem",
  lineHeight: 1.45,
  fontWeight: 400,
  px: 1.25,
  py: 0.75,
  borderRadius: 1,
  maxWidth: 280,
  boxShadow: floatShadow(isDark),
});

// Gold means "alerts", and only alerts: the bell at rest, an outline and a
// wash once they are on. Green stays for primary actions and selection.
export const alertGold = (isDark: boolean) => ({
  glyph: isDark ? "#eab308" : "#a16207",
  outline: isDark ? "rgba(234, 179, 8, 0.45)" : "rgba(161, 98, 7, 0.4)",
  wash: isDark ? "rgba(234, 179, 8, 0.08)" : "rgba(161, 98, 7, 0.06)",
  washHover: isDark ? "rgba(234, 179, 8, 0.12)" : "rgba(161, 98, 7, 0.1)",
});

export const roundedPanelSx = (isDark: boolean) => ({
  backgroundColor: "background.surface",
  border: `1px solid ${panelBorder(isDark)}`,
  borderRadius: 3,
  p: 2,
  minWidth: 0,
});

// The one segmented switch for every chart and workspace control: a quiet
// bordered rail whose selected segment is a primary-tinted wash.
export const workspaceSwitchSx = {
  alignSelf: "flex-start",
  bgcolor: "background.surface",
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  p: 0.25,
  gap: 0.25,
  "& .MuiToggleButton-root": {
    border: "0 !important",
    borderRadius: "4.5px !important",
    px: 1.25,
    minHeight: 28,
    py: 0.25,
    fontSize: "0.8125rem",
    "@media (pointer: coarse)": { minHeight: 44 },
    lineHeight: 1.4,
    textTransform: "none",
    color: "text.secondary",
    fontWeight: 500,
    "&.Mui-selected": {
      color: "primary.main",
      bgcolor: (theme: Theme) =>
        alpha(
          theme.palette.primary.main,
          theme.palette.mode === "dark" ? 0.08 : 0.06,
        ),
      fontWeight: 600,
    },
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: 2,
    },
  },
};

// Phones: workspaceSwitchSx at a slim 32px; an invisible strip above and
// below each button keeps the 44px touch target. Pass both in an sx array so
// this merges into the shared button styles.
export const compactSwitchSx = {
  "& .MuiToggleButton-root": {
    minHeight: 32,
    "@media (pointer: coarse)": { minHeight: 32 },
    fontSize: "0.75rem",
    "&::after": { content: '""', position: "absolute", inset: "-6px 0" },
  },
};

// Small icon controls keep their size on pointer surfaces and grow to the
// 44px touch minimum where the pointer is a finger.
export const touchTargetSx = {
  "@media (pointer: coarse)": { minWidth: 44, minHeight: 44 },
} as const;

export const panelTitleSx = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
} as const;

// The lower band's headers: the same title idiom as the panel rails above,
// but sitting directly on the page — a label line, not a boxed rail — so
// the watchlist and news columns read as part of the page rather than two
// more instruments. Shared so the two headers hold the same optical line.
export const sectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  minHeight: 28,
  mb: 1.25,
  px: 0.25,
} as const;

// The header's footprint (its min-height plus the margin under it), for
// skeletons that reserve a section's real height before it mounts.
export const SECTION_HEADER_HEIGHT = 28 + 10;

// Keyboard-only focus ring for the band's many unstyled controls: `all:
// "unset"` (and explicit `outline: "none"`) also kills the UA's
// :focus-visible outline, so every such control must put one back. Ring on
// keyboard focus only — mouse and touch stay clean.
export const focusRingSx = {
  "&:focus-visible": {
    outline: "2px solid #22c55e",
    outlineOffset: "2px",
  },
} as const;

// Flash a text element to a color, then ease back to its styled color — the
// price-tick flash shared by the market band's cells and the theme
// browser's price cells. DOM mutation, so a tick never re-renders a chart.
export const flashText = (el: HTMLElement | null, color: string) => {
  if (!el) return;
  el.style.transition = "none";
  el.style.color = color;
  window.setTimeout(() => {
    el.style.transition = "color 0.4s ease";
    el.style.color = "";
  }, 600);
};

// One entrance for the whole page: panels and columns rise into place in
// reading order. Transform/opacity only, and off under reduced motion.
export const riseIn = (delayMs: number) =>
  ({
    animation: `overviewRiseIn 0.42s cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms both`,
    "@keyframes overviewRiseIn": {
      from: { opacity: 0, transform: "translateY(10px)" },
      to: { opacity: 1, transform: "none" },
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  }) as const;
